
import React, { useState, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Search as SearchIcon, Plane, Globe } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// Use memo to prevent unnecessary re-renders
export const SearchSection = memo(() => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast({
        title: "Search field is empty",
        description: "Please enter a destination, experience, or host name",
        variant: "destructive",
      });
      return;
    }
    
    // Navigate to search page with the query parameter
    navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div 
      className={cn(
        "bg-white rounded-lg shadow-sm h-full flex flex-col justify-between",
        isMobile ? "p-4" : "p-5 sm:p-6 md:p-8"
      )}
    >
      <div>
        <h1 
          className={cn(
            "font-bold mb-2",
            isMobile ? "text-lg" : "text-2xl md:text-3xl"
          )}
        >
          Find Your Perfect Local Experience
        </h1>
        <p className={cn(
          "text-gray-600 mb-3",
          isMobile ? "text-sm" : "text-lg mb-4"
        )}>
          Connect with amazing local hosts around the world
        </p>
        
        <div className="flex justify-center mb-3 md:mb-6">
          <Button 
            onClick={() => navigate('/search')}
            variant="outline"
            size={isMobile ? "sm" : "default"}
            className="text-primary border-primary hover:bg-primary/5 flex items-center gap-2"
            aria-label="Explore all destinations"
          >
            <Globe className={`${isMobile ? "w-3.5 h-3.5" : "w-4 h-4"}`} />
            Explore All Destinations
          </Button>
        </div>
        
        <form 
          onSubmit={handleSearch} 
          className="flex gap-2 mb-3" 
          role="search"
          itemScope 
          itemType="https://schema.org/SearchAction"
        >
          <meta itemProp="target" content={`${window.location.origin}/search?q={search_term_string}`} />
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" aria-hidden="true" />
            <Input
              ref={inputRef}
              type="search"
              name="search_term_string"
              itemProp="query-input"
              placeholder={isMobile ? "Search destinations..." : "Search destinations, experiences, or hosts..."}
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search destinations, experiences, or hosts"
            />
          </div>
          <Button 
            type="submit" 
            size={isMobile ? "sm" : "default"}
            aria-label="Submit search"
          >
            Search
          </Button>
        </form>
      </div>
      
      <Button 
        variant="ghost" 
        size={isMobile ? "sm" : "default"}
        className="w-full text-gray-600 hover:text-primary hover:bg-primary/5 flex items-center justify-center gap-2 mt-1"
        onClick={() => navigate('/visa-requirements')}
        aria-label="Check visa requirements"
      >
        <Plane className={`${isMobile ? "w-3.5 h-3.5" : "w-4 h-4"}`} aria-hidden="true" />
        Check Visa Requirements
      </Button>
    </div>
  );
});
